/**
 * Based on https://www.joshwcomeau.com/snippets/react-hooks/use-prefers-reduced-motion/
 * with the initial value changed — see below.
 */

import { useState, useEffect } from 'react';

const QUERY = '(prefers-reduced-motion: no-preference)';

function usePrefersReducedMotion() {
  // Always start from the same answer the server gave, never from matchMedia.
  //
  // The original snippet read matchMedia during the initial client render, so
  // a visitor with no motion preference got `false` on the client while the
  // server had rendered `true`. Several components — Nav most visibly — branch
  // on this into structurally different trees, so hydration was handed markup
  // for one tree and asked to mount the other. A production build patches that
  // mismatch silently: the nav links ended up in the logo's div, inheriting its
  // class and losing their own, which rendered them as a bare numbered list
  // stacked in the corner. `gatsby develop` does not server-render, which is
  // why it only ever showed up in production.
  //
  // Starting at `true` also fails safe: nothing animates until we have asked.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(true);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(QUERY);
    const update = () => setPrefersReducedMotion(!mediaQueryList.matches);

    // Read the real value now that hydration has finished.
    update();

    // addEventListener is the modern API; addListener is kept for Safari 13.
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', update);
      return () => mediaQueryList.removeEventListener('change', update);
    }

    mediaQueryList.addListener(update);
    return () => mediaQueryList.removeListener(update);
  }, []);

  return prefersReducedMotion;
}

export default usePrefersReducedMotion;
